// File: frontend/src/pages/ReportFilters.tsx
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import useSWR from 'swr';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Box,
    Typography,
    Autocomplete,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { cyber } from '../theme';
import LanguageIcon from '@mui/icons-material/Language';

interface ReportFiltersProps {
    source: string;
    onSourceChange: (source: string) => void;
    selectedTags: string[];
    onSelectedTagsChange: (tags: string[]) => void;
    timeRange: string;
    onTimeRangeChange: (timeRange: string) => void;
}

// Helper function to format date for display
const formatDateDisplay = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Helper function to parse custom range string
const parseCustomRange = (timeRange: string): { startDate: string; endDate: string } | null => {
    if (timeRange.startsWith('custom:')) {
        const [start, end] = timeRange.replace('custom:', '').split('_');
        return { startDate: start, endDate: end };
    }
    return null;
};

const ReportFilters: React.FC<ReportFiltersProps> = ({
    source,
    onSourceChange,
    selectedTags,
    onSelectedTagsChange,
    timeRange,
    onTimeRangeChange
}) => {
    const { authenticatedFetcher } = useApi();
    const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    const { data: sources, error: sourcesError } = useSWR<string[]>('/api/reports/sources', authenticatedFetcher);
    const { data: tags, error: tagsError } = useSWR<string[]>('/api/reports/tags', authenticatedFetcher);

    const handleTimeRangeChange = (value: string) => {
        if (value === 'custom') {
            // Open the custom date dialog
            const existingCustomRange = parseCustomRange(timeRange);
            if (existingCustomRange) {
                setCustomStartDate(existingCustomRange.startDate);
                setCustomEndDate(existingCustomRange.endDate);
            } else {
                // Default to last 30 days
                const end = new Date();
                const start = new Date();
                start.setDate(start.getDate() - 30);
                setCustomStartDate(start.toISOString().split('T')[0]);
                setCustomEndDate(end.toISOString().split('T')[0]);
            }
            setCustomDateDialogOpen(true);
        } else {
            onTimeRangeChange(value);
        }
    };

    const handleCustomDateApply = () => {
        if (customStartDate && customEndDate) {
            // Format: custom:YYYY-MM-DD_YYYY-MM-DD
            onTimeRangeChange(`custom:${customStartDate}_${customEndDate}`);
            setCustomDateDialogOpen(false);
        }
    };

    const handleCustomDateCancel = () => {
        setCustomDateDialogOpen(false);
    };

    // Determine display value for the select
    const getTimeRangeDisplayValue = () => {
        if (timeRange.startsWith('custom:')) {
            return 'custom';
        }
        return timeRange;
    };

    const customRange = parseCustomRange(timeRange);

    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            
            {/* Source Filter */}
            <Box sx={{ flex: 1, width: '100%' }}>
                <Autocomplete
                    size="small"
                    id="source-filter"
                    options={sources || []}
                    value={source || null}
                    onChange={(_, newValue) => {
                        onSourceChange(newValue || '');
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Source"
                            placeholder="Filter by source"
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option}>
                            <LanguageIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            {option}
                        </li>
                    )}
                    loading={!sources && !sourcesError}
                    disabled={!sources || sources.length === 0}
                    noOptionsText="No sources found"
                />
                {sourcesError && <Typography variant="caption" color="error">Failed to load sources</Typography>}
            </Box>

            {/* Tags Filter */}
            <Box sx={{ flex: 1, width: '100%' }}>
                <Autocomplete
                    multiple
                    size="small"
                    id="tags-filter"
                    options={tags || []}
                    value={selectedTags}
                    onChange={(event, newValue) => {
                        onSelectedTagsChange(newValue);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Tags"
                            placeholder="Filter by tags"
                        />
                    )}
                    loading={!tags && !tagsError}
                    disabled={!tags || tags.length === 0}
                />
                 {tagsError && <Typography variant="caption" color="error">Failed to load tags</Typography>}
            </Box>

            {/* Time Range Filter */}
            <Box sx={{ flex: 1, width: '100%' }}>
                <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel id="time-range-filter-label">Time Range</InputLabel>
                    <Select
                        labelId="time-range-filter-label"
                        id="time-range-filter"
                        value={getTimeRangeDisplayValue()}
                        label="Time Range"
                        onChange={(e) => handleTimeRangeChange(e.target.value as string)}
                    >
                        <MenuItem value=""><em>All Time</em></MenuItem>
                        <MenuItem value="24h">Last 24 Hours</MenuItem>
                        <MenuItem value="7d">Last 7 Days</MenuItem>
                        <MenuItem value="30d">Last 30 Days</MenuItem>
                        <MenuItem value="custom">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" />
                                Custom Range...
                            </Box>
                        </MenuItem>
                    </Select>
                </FormControl>
                {/* Show selected custom range as a chip */}
                {customRange && (
                    <Chip
                        size="small"
                        label={`${formatDateDisplay(customRange.startDate)} - ${formatDateDisplay(customRange.endDate)}`}
                        onDelete={() => onTimeRangeChange('')}
                        sx={{
                            mt: 1,
                            bgcolor: alpha(cyber.primary, 0.15),
                            border: `1px solid ${alpha(cyber.primary, 0.4)}`,
                            color: cyber.primaryLight,
                            '& .MuiChip-icon': { color: cyber.primaryLight },
                            '& .MuiChip-deleteIcon': {
                                color: alpha(cyber.primaryLight, 0.7),
                                '&:hover': { color: cyber.primaryLight },
                            },
                        }}
                        icon={<CalendarTodayIcon />}
                    />
                )}
            </Box>

            {/* Custom Date Range Dialog */}
            <Dialog open={customDateDialogOpen} onClose={handleCustomDateCancel} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon />
                    Select Custom Date Range
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            inputProps={{
                                max: customEndDate || new Date().toISOString().split('T')[0]
                            }}
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            inputProps={{
                                min: customStartDate,
                                max: new Date().toISOString().split('T')[0]
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCustomDateCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCustomDateApply}
                        variant="contained"
                        disabled={!customStartDate || !customEndDate || customStartDate > customEndDate}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default ReportFilters;